// @flow
import {customerIndividualContactMediumsSelector} from '../Individual/selectors';
import {customerSelector} from '../Customer/selectors';
import {fetchCustomerSaga} from '../Customer/sagas';
import {getTrigramFromPostCode} from '../../scenes/SignUp/Address/lib/getInfosFromPostCode';
import {put, call, all, takeEvery, select} from 'redux-saga/effects';
import {setBillingAddress} from '../Customer';
import {setLoading} from '../LoadingStatus/actions';
import {triggerProductOrderPolling} from '../PollingOrder';
import {tripica} from '../../api';
import {updateBillingAddressFailure} from './actions';
import HandleErrorService from '../../lib/handleError';
import I18n from '../../lib/i18n';
import omit from 'lodash/omit';
import Toast from '../../lib/toastService';
import type {BillingAddressActionType} from './actions';
import { loadTaxExemptionByArea } from '../../lib/tripicaStaticConfig';
import WizzeeLog from '../../lib/wizzeeLog';

export function* updateBillingAddressSaga(
  action: BillingAddressActionType,
): Generator<*, *, *> {
  if (action.type !== 'UPDATE_BILLING_ADDRESS') {
    return;
  }

  try {
    const {billingAddress} = action.payload;

    yield put(setLoading('confirmDeliveryAddress', true));
    yield put(setBillingAddress(billingAddress));

    // Get taxExemptionByArea from async storage
    const taxExemptionByArea = yield call(loadTaxExemptionByArea);
    if (taxExemptionByArea) {
      WizzeeLog.log('>>> taxExemptionByArea to apply on customer update', taxExemptionByArea);
    }

    let customer = yield select(customerSelector);
    if (customer) {
      if (customer.fullName) {
        customer = omit(customer, 'fullName');
      }
      if (customer.billingAddress) {
        const trigram = getTrigramFromPostCode(
          customer.billingAddress.postCode,
        );
        if (trigram) {
          customer.billingAddress.stateOrProvince = trigram;
          billingAddress.stateOrProvince = trigram;
        }
      }
    }

    //Back to updateCustomer instead of updateContactMediums because of the taxExemptions
    const response = yield call([tripica, 'updateCustomer'], customer, undefined, undefined, taxExemptionByArea ?? undefined);

    if (!response || !response.errorCodeList) {
      throw new Error('No response or error code list');
    }

    if (response && response.status === 'VALIDATED') {
      const productOrder = yield call(
        triggerProductOrderPolling,
        response.uuid,
        action.payload.errorMapForOrderState,
        true,
      );
      if (productOrder) {
        yield call(fetchCustomerSaga);
      } else {
        throw new Error('product order fetch failed');
      }
    } else {
      throw new Error('status not validated');
    }
    yield call(
      Toast.showMessage,
      I18n.t('Dashboard.UpdateBillingAddress.successTitle'),
    );
  } catch (e) {
    WizzeeLog.warn('[saga] updateBillingAddress', e);
    yield put(updateBillingAddressFailure(e));
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('confirmDeliveryAddress', false));
  }
}

export function* updateBillingAddressDuringProcessSaga(
  action: BillingAddressActionType,
): Generator<*, *, *> {
  if (action.type !== 'UPDATE_BILLING_ADDRESS_DURING_PROCESS') {
    return;
  }

  try {
    let {billingAddress} = action.payload;
    yield put(setLoading('updateBillingAddressDuringProcess', true));
    
    // Get taxExemptionByArea from async storage
    const taxExemptionByArea = yield call(loadTaxExemptionByArea);
    if (taxExemptionByArea) {
      WizzeeLog.log('>>> taxExemptionByArea to apply on customer update', taxExemptionByArea);
    }

    if (billingAddress) {
      const trigram = getTrigramFromPostCode(billingAddress.postCode);
      if (trigram) {
        billingAddress.stateOrProvince = trigram;
      }
    }
    yield put(setBillingAddress(billingAddress));
    const contactMediums = yield select(
      customerIndividualContactMediumsSelector,
    );
    const billingAddressContactMedium = contactMediums?.find(
      contactMedium =>
        contactMedium?.type === 'BILLING_ADDRESS' &&
        tripica.formatter.isAvailableForDate(contactMedium, Date.now()),
    );
    let customer = yield select(customerSelector);
    if (billingAddressContactMedium) {
      yield call(updateBillingAddressSaga, {
        ...action,
        type: 'UPDATE_BILLING_ADDRESS',
      });
    } else {
      const billingContactMedium = {
        medium: {
          city: billingAddress.city,
          country: undefined,
          postCode: billingAddress.postCode,
          stateOrProvince: billingAddress.stateOrProvince,
          street1: billingAddress.street1,
          street2: billingAddress.street2,
          type: 'ADDRESS',
        },
        ouid: undefined,
        prefered: true,
        startDateTime: Date.now(),
        type: 'BILLING_ADDRESS',
      };

      customer.individual.contactMediums = [
        ...customer.individual.contactMediums,
        billingContactMedium,
      ];

      const updatedCustomer = {...customer, billingAddress: billingAddress};
      const response = yield call([tripica, 'updateCustomer'], updatedCustomer, undefined, undefined, taxExemptionByArea ?? undefined);

      if (!response || !response.errorCodeList) {
        throw new Error('No response or error code list');
      }

      if (response && response.status === 'VALIDATED') {
        const productOrder = yield call(
          triggerProductOrderPolling,
          response.uuid,
        );
        if (productOrder.state === 'COMPLETED') {
          //goToNExtPage
        } else {
          throw new Error('validateIndividual product order failed');
        }
      } else {
        throw new Error('status not validated');
      }
    }
  } catch (e) {
    WizzeeLog.warn('[saga] updateBillingAddressDuringProcess', e);
    HandleErrorService.showToastError(e.message);
  } finally {
    yield call(fetchCustomerSaga);
    yield put(setLoading('updateBillingAddressDuringProcess', false));
  }
}

export default function* (): Generator<*, *, *> {
  yield all([takeEvery('UPDATE_BILLING_ADDRESS', updateBillingAddressSaga)]);
  yield all([
    takeEvery(
      'UPDATE_BILLING_ADDRESS_DURING_PROCESS',
      updateBillingAddressDuringProcessSaga,
    ),
  ]);
}
